
import { collection, query, where, limit, getDocs, doc, serverTimestamp, writeBatch, orderBy, updateDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import type { ServiceRegistration, RegistrationStatus } from '../types';
import { injectLogAndNotif } from './firebaseAPI';
import { sanitizeForFirestore } from '../utils/helpers';

const COLLECTION_NAME = 'service_registrations';

export const submitServiceRegistration = async (registration: Omit<ServiceRegistration, 'id'>) => {
    try {
        const batch = writeBatch(db);
        const regRef = doc(collection(db, COLLECTION_NAME));
        const regId = regRef.id;
        
        const sanitizedData = sanitizeForFirestore({ ...registration, id: regId });
        batch.set(regRef, sanitizedData);

        // Tạo thông báo cho Admin & Log
        injectLogAndNotif(batch, {
            actionType: 'CREATE',
            module: 'Registration',
            description: `Yêu cầu dịch vụ mới từ căn ${registration.residentId}: ${registration.type === 'Construction' ? 'Thi công' : 'Phương tiện'}`,
            performedBy: { name: 'Resident', email: registration.residentId },
            ids: [regId],
            linkTo: 'serviceRegistration'
        });

        await batch.commit();
        return regId;
    } catch (error) {
        throw error;
    }
};

/**
 * TỐI ƯU: Sử dụng getDocs thay vì onSnapshot. 
 * Đăng ký dịch vụ không cần cập nhật tức thời theo giây, giúp tiết kiệm chi phí Firestore đáng kể.
 */
export const fetchRegistrations = async (): Promise<ServiceRegistration[]> => {
    const q = query(
        collection(db, COLLECTION_NAME),
        orderBy('date', 'desc'),
        limit(50)
    );

    try {
        const snapshot = await getDocs(q);
        const items = snapshot.docs.map(d => ({ ...d.data() } as ServiceRegistration));
        
        return items.sort((a, b) => {
            if (a.status === 'Pending' && b.status !== 'Pending') return -1;
            if (a.status !== 'Pending' && b.status === 'Pending') return 1;
            return 0;
        });
    } catch (error) {
        console.error("Lỗi fetch registrations:", error);
        return [];
    }
};

export const processRegistrationAction = async (regId: string, status: RegistrationStatus, adminNote: string, adminEmail: string, residentId: string, adminName?: string) => {
    const batch = writeBatch(db);
    batch.update(doc(db, COLLECTION_NAME, regId), {
        status: status,
        rejectionReason: adminNote,
        processedBy: adminEmail,
        processedAt: new Date().toISOString()
    });

    // Thông báo cho cư dân
    const resNotifRef = doc(collection(db, 'notifications'));
    batch.set(resNotifRef, {
        userId: residentId,
        title: `Yêu cầu dịch vụ ${status === 'Approved' ? 'ĐÃ DUYỆT' : 'BỊ TỪ CHỐI'}`,
        body: adminNote || `BQL đã xử lý yêu cầu của bạn.`,
        type: 'system',
        link: 'portalContact',
        isRead: false,
        createdAt: serverTimestamp()
    });

    // Log cho admin
    injectLogAndNotif(batch, {
        actionType: 'UPDATE',
        module: 'Registration',
        description: `Xử lý yêu cầu căn ${residentId}. Kết quả: ${status}`,
        performedBy: { name: adminName || 'Admin', email: adminEmail },
        ids: [regId],
        linkTo: 'serviceRegistration'
    });

    await batch.commit();
};

/**
 * Xoá tài liệu đính kèm để tiết kiệm dung lượng (sau khi đã xử lý xong)
 */
export const deleteRegistrationMedia = async (regId: string) => {
    try {
        const regRef = doc(db, COLLECTION_NAME, regId);
        await updateDoc(regRef, {
            documents: []
        });
    } catch (error) {
        console.error("Lỗi xoá tài liệu registration:", error);
        throw error;
    }
};
